import { tooltipClasses } from '@mui/material/Tooltip'
import { Theme } from '@mui/material/styles'

import {
  colorTokens,
  layout,
  motion,
  radius as radiusTokens,
  space,
  typography as typeTokens,
} from '../tokens.generated'

// ----------------------------------------------------------------------
// A tooltip is a dark tile with caption text, on the control radius. It is a
// hint, not a label: a control whose meaning lives only in its tooltip has no
// meaning on a touch device.
//
// **Dark in both schemes, and the words stated with it.** The tile used to be
// a step on the grey ramp, picked by scheme, with MUI's white for the words.
// The ramp is by role and inverts in dark, so the step that was a dark grey in
// light was a light one in dark, and every hint in the dark console was white
// on pale grey at 1.5:1. The pair is the token source's `tooltip` and
// `onTooltip` now, so the tile and its words cannot be chosen apart.
//
// **One inset, on all four sides, and the words centred in it.** MUI ships
// `4px 8px`, twice as much horizontal as vertical, so a one-line hint read
// as a wide strip with its words pressed against the top edge, and a two-line
// one had less air above it than beside it. Equal inset is also what makes a
// single line vertically centred without a flex box to do it.
//
// The line height is stated rather than inherited. Unstated, a 12px tooltip
// took whatever body line box it happened to be rendered under, so the same
// words sat at a different height depending on what they were hovered from.
//
// **The words are never bold.** MUI sets its tile at the theme's medium
// weight, which this theme makes the semibold, 600, and nothing here stated
// the caption step's own weight, so every hint in the console was set
// semibold beside the regular text of the control it described. Asked for on 2026-10-22: "Let's make
// sure that text shown inside tooltips is not bold (across the site)." The
// weight is the type step's, and emphasis inside a hint (`strong`, `b`) takes
// the tile's weight rather than the browser's bold.
//
// The touch variant states its own inset rather than winning by merge order.
// Theme `styleOverrides` are composed after the component's own styles, so the
// `padding` above already overrides MUI's `8px 16px`, which is the right
// outcome and nowhere says so. An override that wins silently is the class of
// thing `preview:audit` exists because of.
//
// **Every tooltip points at what it describes, from close by.** MUI draws no
// arrow unless a screen asks, and stands the tile 14px off its anchor, so in a
// grid of controls a hint was about as near the next control as its own. On
// the map callout's command dial the hint for Wake sat on Honk, one row down,
// and was read as Honk's. So the arrow is on by default, centred on the side
// facing the anchor, and the tile stands `tooltipOffset` off, which puts the
// arrow's tip about 3px from the anchor's edge: near enough to belong to it,
// never over it. The arrow is sized in pixels from `tooltipArrow` rather than
// in MUI's `1em` of whatever the tile's type is, because the arrow's own
// offsets are written in `em` and would otherwise move with the touch type.
//
// The distances are per side, in the same selectors MUI writes its own 14px
// with, so they replace MUI's rather than adding to it. A finger is still on
// the control when its tooltip opens, so the touch tile states its own,
// larger distance, again rather than inheriting one by merge order.
//
// The arrow never reaches the tile's rounded corner. Popper slides it along
// the edge to stay on the anchor's centre, and MUI stops it 4px from the end,
// inside a 10px corner, where the edge it hangs from is already curving away.
// Stopping it a corner radius short keeps it on the straight part.
//
// **Only the tile takes the pointer, and only while it is open.** MUI's
// tooltip can be hovered, its answer to WCAG 1.4.13, and it gave the pointer
// to its whole popper: the tile, and the transparent stand-off between the
// tile and the control, as wide as the tile. Measured in the preview on
// 2026-10-19, that box took the pointer where nothing of the hint is drawn.
// Chrome hit-tests a point as the pixel square to its right and below it, so
// a box meeting a control's bottom or right edge owns the control's last
// row: with Dark mode's hint open, the pointer at 45 on a button ending at
// 45.5 was on the hint, the hand turned to the arrow, the fill went out and a
// click there did nothing, on every control whose hint opens below it. And
// the stand-off lay unseen over whatever was beside the control: a tag
// chip's over Add tag, the sidebar footer's over General reports. That is the
// cursor that "glitches when hovering over buttons".
//
// So the popper passes the pointer to what is under it and the tile, its
// arrow with it, takes it while the hint is open. The tile stands
// `tooltipOffset` off, so nothing that takes the pointer meets the control,
// and a tile that is leaving fades with the pointer passing through it. The
// stand-off used to carry the pointer from the control onto the tile, and
// `motion.dwell.hintLeave` does that now: the hint waits that long after the
// pointer leaves its control, enough to cross the stand-off, and a hint the
// pointer reaches stays while it is on it. A drawn tile still takes what it
// lies over while it is open: that is a hint being read, and it is seen.
// ----------------------------------------------------------------------

/** Which margin of the tile faces the anchor, per side the tile opens on. */
const FACING = {
  top: 'marginBottom',
  bottom: 'marginTop',
  left: 'marginRight',
  right: 'marginLeft',
} as const

/** The tile's distance from its anchor, on whichever side Popper put it. */
const standOff = (distance: number) =>
  Object.fromEntries(
    Object.entries(FACING).map(([side, margin]) => [
      `.${tooltipClasses.popper}[data-popper-placement*="${side}"] &`,
      { [margin]: `${distance}px` },
    ]),
  )

/** Popper's arrow modifier, kept a corner radius clear of the tile's ends. */
const ARROW_MODIFIER = {
  name: 'arrow',
  options: { padding: radiusTokens.console.control },
}

/**
 * The room a tooltip keeps from the screen's edge. Unset, a 300px hint on a
 * phone stood flush with the edge, its text touching the glass (found on
 * 2026-10-31).
 */
const EDGE_MODIFIER = {
  name: 'preventOverflow',
  options: { padding: space.s2 },
}

export default function Tooltip(theme: Theme) {
  const colors = colorTokens[theme.palette.mode]

  return {
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        // Long enough to cross the stand-off onto the tile (see above).
        leaveDelay: motion.dwell.hintLeave,
        PopperProps: {
          popperOptions: { modifiers: [ARROW_MODIFIER, EDGE_MODIFIER] },
        },
      },
      styleOverrides: {
        // Only what is drawn takes the pointer, and only while it is open
        // (see above). `open` is the popper's own prop: MUI turns the popper
        // off for the pointer while it leaves, and the tile has to follow,
        // since it states its own value rather than inheriting one.
        popperInteractive: ({ open }: { open?: boolean }) => ({
          pointerEvents: 'none' as const,
          [`& .${tooltipClasses.tooltip}`]: {
            pointerEvents: open ? ('auto' as const) : ('none' as const),
          },
        }),
        tooltip: {
          'backgroundColor': colors.tooltip,
          'color': colors.onTooltip,
          'borderRadius': theme.radii.control,
          'fontSize': theme.typography.pxToRem(typeTokens.console.caption.size),
          'fontWeight': typeTokens.console.caption.weight,
          'lineHeight': typeTokens.console.caption.lineHeight,
          'padding': theme.spacing(1),
          'textAlign': 'center' as const,
          ...standOff(layout.tooltipOffset),
          // Emphasis inside a hint is not bold either (see above).
          '& strong, & b': { fontWeight: 'inherit' },
          // A finger needs a bigger tile than a pointer does, and this is the
          // one place the equal inset grows rather than the type.
          [`&.${tooltipClasses.touch}`]: {
            padding: theme.spacing(1.5),
            fontSize: theme.typography.pxToRem(typeTokens.console.small.size),
            fontWeight: typeTokens.console.small.weight,
            lineHeight: typeTokens.console.small.lineHeight,
            ...standOff(layout.tooltipTouchOffset),
          },
        },
        arrow: {
          color: colors.tooltip,
          fontSize: `${layout.tooltipArrow}px`,
        },
      },
    },
  }
}
